<script lang="ts">
    import { onMount } from "svelte";
    import { get } from "svelte/store";
    import { Roarr as log } from "roarr";

    import TileBoard from "../components/TileBoard.svelte";
    import ChatBox from "../components/ChatBox.svelte";
    import PlayerList from "../components/PlayerList.svelte";
    import type { GridStackEvent } from "../components/GridStackBoard.svelte";

    import { TileToReadableString } from "../misc/boardTypes";
    import {
        getRuntimeTrayLayout,
        getTraySlotFromIndex,
    } from "../misc/trayLayout";
    import { sortByColour, sortByTileType } from "../misc/tileSorters";
    import type { ChatMessage } from "../types/chat";
    import type { Tile, GridStackTile } from "../types/tile";
    import { websocket } from "../store/websocket";
    import {
        name,
        players,
        server,
        initialTiles,
        currentPlayer,
        isReady,
        initialMeld,
    } from "../store/userdata";
    import {
        successToast,
        infoToast,
        warningToast,
        errorToast,
    } from "../misc/toasts";
    import { navigateTo } from "../misc/routes";

    let tableTiles: GridStackTile[] = [];
    let trayTiles: GridStackTile[] = [];
    let messages: ChatMessage[] = [];
    $: isOurTurn = $currentPlayer === $name;

    let isBagEmpty: boolean;
    $: {
        let totalNoOfTiles = tableTiles.length;
        for (const tiles of Object.values(numberOfTiles)) {
            totalNoOfTiles += tiles;
        }
        isBagEmpty = totalNoOfTiles >= 106;
    }

    let isGameOver: boolean = false;
    let isStalemate: boolean = false;
    let didWeWin: boolean = false;

    let dirty = false; // Set to true when player does something on the board
    let numberOfTiles: { [playerName: string]: number } = {};

    let offlinePlayers: string[] = [];

    let showChatBox = false;
    let notification = false;
    let playMode = false;
    let boardScale = "normal";
    let showSidePanel = false;
    let showTableChrome = true;
    let showTrayHelp = true;
    let showWelcomeCard = true;
    let showPlayersCard = true;
    let showBagPanel = true;
    let showChatControl = true;

    $: effectiveShowSidePanel = showSidePanel && !playMode;
    $: effectiveShowTableChrome = showTableChrome && !playMode;
    $: effectiveShowTrayHelp = showTrayHelp && !playMode;

    // Populates number of tiles
    for (const player of $players) {
        numberOfTiles = {
            ...numberOfTiles,
            [player]: 14,
        };
    }

    // Adds initial tiles
    for (const initialTile of $initialTiles) {
        const slot = getTraySlotFromIndex(trayTiles.length);
        const gridStackTile: GridStackTile = {
            x: slot.x,
            y: slot.y,
            colour: initialTile.Colour,
            tileType: initialTile.TileType,
            staging: false,
        };
        trayTiles = [...trayTiles, gridStackTile];
    }

    let canMoveTableTile = async (e: GridStackEvent) => {
        const deltaX = e.dst.x - e.src.x;
        const deltaY = e.dst.y - e.src.y;

        try {
            log.info(
                `Asking if we can move tile from ${e.src.x},${e.src.y} to ${e.dst.x},${e.dst.y}...`
            );

            // Ask server if we can do that
            await websocket.mayIMoveTile(e.src.x, e.src.y, deltaX, deltaY);
        } catch (e) {
            const errorMsg = `Can't move tile because: ${e}`;
            log.warn(errorMsg);
            errorToast(errorMsg);
            return false;
        }

        log.info("Moved tile on table!");
        dirty = true;
        return true;
    };

    let canAddTile = async (e: GridStackEvent) => {
        if (isGameOver) {
            log.warn("Cannot add tile because the game is over.");
            return false;
        }

        const trayGridStackTile: GridStackTile = trayTiles.find(
            (trayTile) => trayTile.x === e.src.x && trayTile.y === e.src.y
        );

        if (!trayGridStackTile) {
            const errorMsg = `Cannot find tray tile with position ${e.src.x},${e.src.y}`;
            log.warn(errorMsg);
            errorToast(errorMsg);
            return false;
        }

        const trayTile: Tile = {
            Colour: trayGridStackTile.colour,
            TileType: trayGridStackTile.tileType,
        };

        try {
            const tileStr = TileToReadableString(trayTile);
            log.info(
                `Asking server if we can place ${tileStr} tile at ${e.dst.x},${e.dst.y}...`
            );

            await websocket.mayIAddTile(trayTile, e.dst.x, e.dst.y);
        } catch (e) {
            const errorMsg = `Can't add tile because: ${e}`;
            log.warn(errorMsg);
            errorToast(errorMsg);
            return false;
        }

        log.info("Added tile to table!");

        dirty = true;
        return true;
    };

    let canRemoveTile = async (e: GridStackEvent) => {
        const tableGridStackTile: GridStackTile = tableTiles.find(
            (tableTile) => tableTile.x === e.src.x && tableTile.y === e.src.y
        );

        if (!tableGridStackTile) {
            const errorMsg = `Cannot find tray tile with position ${e.src.x},${e.src.y}`;
            log.warn(errorMsg);
            errorToast(errorMsg);
            return false;
        }

        const tableTile: Tile = {
            Colour: tableGridStackTile.colour,
            TileType: tableGridStackTile.tileType,
        };

        try {
            const tileStr = TileToReadableString(tableTile);
            log.info(`Asking server if we can remove ${tileStr}`);

            await websocket.mayIRemoveTile(e.src.x, e.src.y);
        } catch (e) {
            const errorMsg = `Can't remove tile because: ${e}`;
            log.warn(errorMsg);
            errorToast(errorMsg);
            return false;
        }

        log.info("Removed tile from table!");

        dirty = true;
        return true;
    };

    function onLeaveServerClick() {
        const answer = window.confirm(
            "Are you sure you want to leave the server?"
        );

        if (answer) {
            websocket.close();
            navigateTo("/");
        }
    }

    function onReturnToLobbyClick() {
        navigateTo("/lobby");
    }

    const onResetBoardClick = async () => {
        try {
            log.info("Asking the server if we can reset the board...");
            await websocket.mayIResetBoard();
        } catch (e) {
            const errorMsg = `Can't reset board because: ${e}`;
            log.warn(errorMsg);
            errorToast(errorMsg);
            return;
        }

        log.info("Reset the board!");
        infoToast("You have reset your changes.");
        dirty = false;
        return;
    };

    function onMessageSubmit(e: CustomEvent<string>) {
        const message = e.detail;
        websocket.sendChatMessage(message);

        log.info(`Sent chat message in-game: ${message}`);
    }

    const onEndTurn = async () => {
        try {
            log.info("Asking the server if we can end our turn...");
            const newTile = await websocket.mayIEndTurn();

            log.info("Ended our turn!");

            // If we've picked a new tile, add to the tray
            if (newTile) {
                // Find a free position
                const freePos = findEmptyTileSlot();

                // Put the tile at that position
                const gridStackTile: GridStackTile = {
                    x: freePos.x,
                    y: freePos.y,
                    colour: newTile.Colour,
                    tileType: newTile.TileType,
                    staging: false,
                };
                trayTiles = [...trayTiles, gridStackTile];

                const tileStr = TileToReadableString(newTile);
                log.info(`Got a new tile: ${tileStr}`);
            } else {
                // We must've made a valid go; switch off initial meld
                $initialMeld = false;
            }
        } catch (e) {
            const errorMsg = `Can't end turn because: ${e}`;
            log.warn(errorMsg);
            errorToast(errorMsg);
            return;
        }

        // Unstages all tiles and undirties
        tableTiles = tableTiles.map((tableTile) => ({
            ...tableTile,
            staging: false,
        }));
        dirty = false;
    };

    const findEmptyTileSlot = () => {
        // Create set of tray tile position hashes
        const trayTilePosHashes = new Set();
        for (const trayTile of trayTiles) {
            const trayTileHash = 2 ** trayTile.x * 3 ** trayTile.y;
            trayTilePosHashes.add(trayTileHash);
        }

        // Look for position that isn't hashed
        const { columns, rows } = getRuntimeTrayLayout();
        for (let y = 0; y < rows; y++) {
            for (let x = 0; x < columns; x++) {
                const positionHash = 2 ** x * 3 ** y;
                if (!trayTilePosHashes.has(positionHash)) return { x, y };
            }
        }

        log.error("There is no more space on the tray! Defaulting to (0,0).");
        return { x: 0, y: 0 };
    };

    const onShowChatBox = () => {
        showChatBox = !showChatBox;
        notification = false;
    };

    const onPlayModeToggle = () => {
        playMode = !playMode;

        if (playMode) {
            showChatBox = false;
        }
    };

    onMount(() => {
        if (!get(websocket)) {
            log.warn("Game route mounted without an active websocket.");
            return;
        }

        // When we receive a new chat message
        websocket.onReceiveChatMessage((senderName, chatMessage) => {
            log.info(
                `Received chat message from ${senderName} in-game: ${chatMessage}`
            );

            messages = [
                ...messages,
                { name: senderName, message: chatMessage },
            ];

            // If chat window is closed, show notification
            if (!showChatBox) {
                notification = true;
            }
        });

        // When someone's put down a tile
        websocket.onTheyAddedTile((tile, x, y) => {
            const tileStr = TileToReadableString(tile);
            log.info(
                `${$currentPlayer} just added a ${tileStr} tile to the table at ${x},${y}`
            );

            const gridStackTile: GridStackTile = {
                x,
                y,
                colour: tile.Colour,
                tileType: tile.TileType,
                staging: true,
            };
            tableTiles = [...tableTiles, gridStackTile];
        });

        // When someone's moved a tile
        websocket.onTheyMovedTile((x, y, deltaX, deltaY) => {
            const newX = x + deltaX;
            const newY = y + deltaY;

            log.info(
                `${$currentPlayer} just moved a tile from ${x},${y} to ${newX},${newY}`
            );

            for (let i = 0; i < tableTiles.length; i++) {
                if (tableTiles[i].x === x && tableTiles[i].y === y) {
                    tableTiles[i].x = newX;
                    tableTiles[i].y = newY;
                    break;
                }
            }
        });

        // When someone's removed a tile
        websocket.onTheyRemovedTile((x, y) => {
            log.info(`${$currentPlayer} just removed a tile from ${x},${y}`);

            tableTiles = tableTiles.filter(
                (tableTile) => !(tableTile.x === x && tableTile.y === y)
            );
        });

        // When we need to reset the board
        websocket.onResetBoard((boardTiles) => {
            tableTiles = [];
            tableTiles = boardTiles.map((boardTile) => ({
                colour: boardTile.Colour,
                tileType: boardTile.TileType,
                x: boardTile.X,
                y: boardTile.Y,
                staging: false,
            }));

            log.info("Board has been reset to:");
            console.info(boardTiles);
        });

        // When we need to update our tray
        websocket.onUpdateTray((trayTilesToAddBack) => {
            // Add new tray tiles
            for (const newTrayTile of trayTilesToAddBack) {
                const newPos = findEmptyTileSlot();
                const newGridstackTrayTile: GridStackTile = {
                    colour: newTrayTile.Colour,
                    tileType: newTrayTile.TileType,
                    x: newPos.x,
                    y: newPos.y,
                    staging: false,
                };

                trayTiles = [...trayTiles, newGridstackTrayTile];
            }

            log.info("Tray has been reset to:");
            console.info(trayTiles);
        });

        // When we need to update someone's tile count
        websocket.onSetTileCount((player, tileCount) => {
            numberOfTiles[player] = tileCount;

            log.info(`Tile count of ${player} set to ${tileCount}`);
        });

        // When someone has ended their turn
        websocket.onNextPlayer((nextPlayer) => {
            // Unstages all tiles
            tableTiles = tableTiles.map((tableTile) => ({
                ...tableTile,
                staging: false,
            }));

            log.info(`It is now ${nextPlayer}'s turn`);

            $currentPlayer = nextPlayer;

            // If it's our turn, alert with a toast
            if ($currentPlayer === $name) {
                infoToast("It is now your turn!");
            }
        });

        // When someone disconnects
        websocket.onLostPlayer((lostPlayerName) => {
            log.info(`We've lost ${lostPlayerName}`);
            offlinePlayers = [...offlinePlayers, lostPlayerName];
        });

        // When someone reconnects
        websocket.onNewPlayer((newPlayerName) => {
            log.info(`We've found ${newPlayerName}`);

            // If they're an offline player, add them back
            if (offlinePlayers.includes(newPlayerName)) {
                offlinePlayers = offlinePlayers.filter(
                    (name) => name !== newPlayerName
                );
            }

            // If game is over, add them back for the lobby
            const newPlayerAlreadyExists = $players.includes(newPlayerName);
            if (isGameOver && !newPlayerAlreadyExists) {
                $players = [...$players, newPlayerName];
            }
        });

        // When someone wins
        websocket.onGameFinish((winner) => {
            isGameOver = true;

            if (!winner) {
                isStalemate = true;
                log.info("It's a stalemate!");
            } else {
                didWeWin = winner === $name;

                if (didWeWin) {
                    log.info("You won the game!");
                    successToast("You won the game!");
                } else {
                    log.info(`${winner} won the game, and we lost.`);
                    warningToast(
                        `${winner} won the game. Better luck next time, buddy.`
                    );
                }
            }

            // Remove players that have disconnected
            $players = $players.filter(
                (player) => !offlinePlayers.includes(player)
            );

            // Unstages all tiles
            tableTiles = tableTiles.map((tableTile) => ({
                ...tableTile,
                staging: false,
            }));

            // Prepare for readying users
            $isReady = new Set();
            websocket.onTheyAreReady((readyPlayer) => {
                log.info(`${readyPlayer} has readied up.`);
                $isReady = $isReady.add(readyPlayer);
            });
        });

        return () => websocket.unsubscribeFromAll();
    });
</script>

<main
    id="game"
    class:playMode={playMode}
    class:hideSidebar={!effectiveShowSidePanel}
    class:scaleCompact={boardScale === "compact"}
    class:scaleLarge={boardScale === "large"}
>
    <section class="control-dock" aria-label="game controls">
        <div class="turn-summary">
            <div class="meta-strip">
                {#if $initialMeld}
                    <span class="pill yellow initial-meld-tooltip">INITIAL MELD</span>
                {/if}
                {#if isBagEmpty}
                    <span class="pill red bag-empty-tooltip">BAG EMPTY</span>
                {/if}
            </div>

            <p>
                {#if isGameOver && isStalemate}
                    Stalemate.
                {:else if isGameOver && didWeWin}
                    You won.
                {:else if isGameOver && !didWeWin}
                    {$currentPlayer} won.
                {:else if isOurTurn}
                    Your turn.
                {:else}
                    {$currentPlayer}'s turn.
                {/if}
            </p>
        </div>

        <div class="quick-actions">
            {#if isGameOver}
                <button class="primary-action compact-action" on:click={onReturnToLobbyClick}>Return to lobby</button>
            {:else}
                <button
                    class="primary-action compact-action"
                    on:click={onEndTurn}
                    disabled={!isOurTurn || isGameOver}
                >End turn</button>
            {/if}
            <button class="secondary-action compact-action" on:click={onResetBoardClick} disabled={!dirty}>Reset board</button>
            <button class="sort-button compact-action" on:click={() => (trayTiles = sortByColour(trayTiles))}>Sort colour</button>
            <button class="sort-button compact-action" on:click={() => (trayTiles = sortByTileType(trayTiles))}>Sort type</button>
            <button class="mode-button compact-action" class:activeMode={playMode} on:click={onPlayModeToggle}>
                {playMode ? "Exit play view" : "Optimize play"}
            </button>
        </div>

        <details class="view-options">
            <summary>View</summary>
            <div class="view-options-panel">
                <label>
                    <span>Scale</span>
                    <select bind:value={boardScale}>
                        <option value="compact">Compact</option>
                        <option value="normal">Normal</option>
                        <option value="large">Large</option>
                    </select>
                </label>
                <label>
                    <input type="checkbox" bind:checked={showSidePanel} disabled={playMode} />
                    <span>Side panel</span>
                </label>
                <label>
                    <input type="checkbox" bind:checked={showTableChrome} disabled={playMode} />
                    <span>Board labels</span>
                </label>
                <label>
                    <input type="checkbox" bind:checked={showTrayHelp} disabled={playMode} />
                    <span>Tray labels</span>
                </label>
                <label>
                    <input type="checkbox" bind:checked={showWelcomeCard} />
                    <span>Welcome card</span>
                </label>
                <label>
                    <input type="checkbox" bind:checked={showPlayersCard} />
                    <span>Players card</span>
                </label>
                <label>
                    <input type="checkbox" bind:checked={showBagPanel} />
                    <span>Bag card</span>
                </label>
                <label>
                    <input type="checkbox" bind:checked={showChatControl} disabled={playMode} />
                    <span>Chat button</span>
                </label>
            </div>
        </details>

        <button id="leave-btn" class="leave-button compact-action" on:click={onLeaveServerClick}>Leave</button>
    </section>

    <section class="table-column">
        <TileBoard
            bind:tableTiles
            bind:trayTiles
            {canMoveTableTile}
            {canAddTile}
            {canRemoveTile}
            showTableChrome={effectiveShowTableChrome}
            showTrayHelp={effectiveShowTrayHelp}
            enableTableTileMovement={isOurTurn && !isGameOver}
        />

        <div id="buttons">
            <div id="sorting">
                <button class="sort-button" on:click={() => (trayTiles = sortByColour(trayTiles))}>Sort by colour</button>
                <button class="sort-button" on:click={() => (trayTiles = sortByTileType(trayTiles))}>Sort by tile type</button>
            </div>
        </div>
    </section>

    {#if effectiveShowSidePanel}
    <aside id="social">
        <div class="meta-strip">
            {#if $initialMeld}
                <span class="pill yellow initial-meld-tooltip">INITIAL MELD</span>
            {/if}
            {#if isBagEmpty}
                <span class="pill red bag-empty-tooltip">BAG EMPTY</span>
            {/if}
        </div>

        {#if showWelcomeCard}
            <div class="welcome-card">
                <p class="eyebrow">Live server</p>
                <p class="welcome-line">Welcome to {$server}, {$name}.</p>
            </div>
        {/if}

        {#if isGameOver && isStalemate}
            <div class="status-card yellow">
                <strong>It's a stalemate.</strong>
                <span>No one could draw a decisive finish.</span>
            </div>
        {:else if isGameOver && didWeWin}
            <div class="status-card yellow">
                <strong>You won!</strong>
                <span>The table is yours.</span>
            </div>
        {:else if isGameOver && !didWeWin}
            <div class="status-card red">
                <strong>You lost this round.</strong>
                <span>Reset and queue for the next deal.</span>
            </div>
        {:else if isOurTurn}
            <div class="status-card green">
                <strong>It's your turn now.</strong>
                <span>Make your move and lock the board.</span>
            </div>
        {:else}
            <div class="status-card neutral">
                <strong>{$currentPlayer} is playing.</strong>
                <span>Watch the staged tiles and stay ready.</span>
            </div>
        {/if}

        <div id="actions">
            {#if isGameOver}
                <button class="primary-action" on:click={onReturnToLobbyClick}>Return to lobby</button>
            {:else}
                <button
                    class="primary-action"
                    on:click={onEndTurn}
                    disabled={!isOurTurn || isGameOver}
                >End turn</button>
            {/if}

            <button class="secondary-action" on:click={onResetBoardClick} disabled={!dirty}>Reset board</button>
        </div>

        {#if showPlayersCard}
            <PlayerList
                players={$players}
                currentPlayer={$currentPlayer}
                {numberOfTiles}
                {offlinePlayers}
            />
        {/if}

        {#if showBagPanel}
            <div class="bag-panel">
                <strong>{isBagEmpty ? "Bag is empty" : "Bag is not empty"}</strong>
                <span>{isBagEmpty ? "Ending your turn will skip your go." : "End turn will draw a tile."}</span>
            </div>
        {/if}
    </aside>
    {/if}

    <div id="chatbox-container" class:hidden={!showChatBox || !showChatControl || playMode}>
        <ChatBox bind:messages on:submitMessage={onMessageSubmit} />
    </div>
    {#if showChatControl && !playMode}
        <button
            id="chat-btn"
            class:notification
            on:click={onShowChatBox}
        >Chat</button>
    {/if}
</main>

<style>
    #game {
        --game-edge: 14px;
        --tray-reserved-space: 320px;
        --tray-grid-height: 166px;
        --tile-face-height: 82px;
        --tile-value-size: 2.05rem;
        --tile-joker-size: 1.35rem;
        --tile-face-radius: 7px;
        --board-shell-pad: 14px;
        --tray-shell-pad: 12px;
        --tray-shell-pad-compact: 10px;
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        grid-template-rows: auto minmax(0, 1fr);
        gap: 12px;
        min-height: 100vh;
        padding: var(--game-edge) var(--game-edge) var(--tray-reserved-space);
        position: relative;
    }

    #game.scaleCompact {
        --tray-reserved-space: 266px;
        --tray-grid-height: 148px;
        --tile-face-height: 74px;
        --tile-value-size: 1.78rem;
        --tile-joker-size: 1.18rem;
        --board-shell-pad: 10px;
        --tray-shell-pad: 10px;
    }

    #game.scaleLarge {
        --tray-reserved-space: 360px;
        --tray-grid-height: 196px;
        --tile-face-height: 98px;
        --tile-value-size: 2.42rem;
        --tile-joker-size: 1.62rem;
    }

    #game.playMode {
        --tray-reserved-space: 194px;
        --tray-grid-height: 150px;
        --board-shell-pad: 10px;
        --tray-shell-pad: 10px;
    }

    .control-dock {
        display: grid;
        grid-template-columns: minmax(160px, auto) minmax(0, 1fr) auto auto;
        gap: 10px;
        align-items: center;
        position: sticky;
        top: var(--game-edge);
        z-index: 12;
        padding: 10px;
        border: 1px solid rgba(126, 173, 255, 0.18);
        border-radius: var(--radius-lg);
        background:
            linear-gradient(180deg, rgba(12, 28, 50, 0.96), rgba(6, 15, 29, 0.96));
        box-shadow: var(--shadow-soft);
    }

    .turn-summary {
        display: grid;
        gap: 6px;
        min-width: 0;
    }

    .turn-summary p {
        margin: 0;
        font-size: 1rem;
        font-weight: 700;
        color: var(--text-main);
    }

    .quick-actions {
        display: flex;
        gap: 8px;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: wrap;
        min-width: 0;
    }

    .compact-action {
        min-height: 44px;
        padding: 0.68rem 0.9rem;
        font-size: 0.92rem;
        white-space: nowrap;
        box-shadow: none;
    }

    .mode-button {
        background:
            linear-gradient(180deg, rgba(255, 207, 99, 0.96), rgba(170, 106, 34, 0.98));
        border-color: rgba(255, 216, 122, 0.52);
        color: #271806;
        font-weight: 700;
    }

    .mode-button.activeMode {
        background:
            linear-gradient(180deg, rgba(115, 235, 124, 0.95), rgba(33, 119, 57, 0.98));
        color: #041707;
    }

    .view-options {
        position: relative;
    }

    .view-options summary {
        list-style: none;
        min-height: 44px;
        display: grid;
        place-items: center;
        padding: 0 0.95rem;
        border: 1px solid rgba(123, 162, 222, 0.24);
        border-radius: var(--radius-md);
        background: rgba(10, 23, 42, 0.96);
        cursor: pointer;
        user-select: none;
    }

    .view-options summary::-webkit-details-marker {
        display: none;
    }

    .view-options-panel {
        position: absolute;
        right: 0;
        top: calc(100% + 8px);
        width: 230px;
        display: grid;
        gap: 10px;
        padding: 12px;
        border: 1px solid rgba(126, 173, 255, 0.18);
        border-radius: var(--radius-lg);
        background: rgba(7, 17, 31, 0.98);
        box-shadow: var(--shadow-strong);
    }

    .view-options-panel label {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        color: var(--text-muted);
        font-size: 0.9rem;
    }

    .view-options-panel label:first-child {
        display: grid;
        align-items: start;
    }

    .view-options-panel input[type="checkbox"] {
        width: 18px;
        height: 18px;
        padding: 0;
    }

    .view-options-panel select {
        width: 100%;
        min-height: 40px;
        padding: 0.45rem 0.55rem;
    }

    .table-column {
        display: flex;
        flex-direction: column;
        gap: 10px;
        min-width: 0;
    }

    #social {
        display: flex;
        flex-direction: column;
        gap: 14px;
        padding: 18px;
        position: fixed;
        top: 92px;
        right: var(--game-edge);
        bottom: var(--tray-reserved-space);
        width: min(320px, calc(100vw - 2 * var(--game-edge)));
        z-index: 8;
        overflow: auto;
        border-radius: var(--radius-lg);
        border: 1px solid rgba(126, 173, 255, 0.16);
        background:
            radial-gradient(circle at top, rgba(63, 140, 255, 0.14), transparent 34%),
            linear-gradient(180deg, rgba(255, 197, 90, 0.04), transparent 14%),
            linear-gradient(180deg, rgba(11, 26, 47, 0.96), rgba(6, 16, 31, 0.96));
        box-shadow:
            var(--shadow-soft),
            inset 0 1px 0 rgba(255, 255, 255, 0.04);
    }

    #buttons {
        display: none;
    }

    #sorting {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
        justify-content: flex-end;
    }

    .leave-button {
        background: rgba(67, 17, 23, 0.86);
        border-color: rgba(255, 118, 111, 0.24);
        color: #ff978f;
    }

    .sort-button {
        background: rgba(11, 28, 52, 0.92);
        border-color: rgba(255, 207, 99, 0.26);
        color: #ffd56b;
    }

    .meta-strip {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
    }

    .welcome-card,
    .status-card,
    .bag-panel {
        border-radius: var(--radius-lg);
        border: 1px solid rgba(126, 173, 255, 0.16);
        background:
            linear-gradient(180deg, rgba(19, 39, 68, 0.74), rgba(10, 24, 44, 0.88));
        padding: 16px 18px;
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
    }

    .eyebrow {
        margin: 0 0 8px;
        color: var(--text-dim);
        font-size: 0.78rem;
        text-transform: uppercase;
        letter-spacing: 0.14em;
    }

    .welcome-line {
        margin: 0;
        color: var(--text-main);
        line-height: 1.55;
        font-size: 1.05rem;
    }

    .status-card {
        display: grid;
        gap: 8px;
        box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.02);
    }

    .status-card strong,
    .bag-panel strong {
        font-size: 1.05rem;
    }

    .status-card span,
    .bag-panel span {
        color: var(--text-muted);
        line-height: 1.5;
    }

    #actions {
        display: grid;
        grid-template-columns: 1fr;
        gap: 12px;
    }

    .primary-action {
        background:
            linear-gradient(180deg, rgba(59, 128, 255, 0.98), rgba(21, 63, 133, 0.98));
        border-color: rgba(123, 176, 255, 0.42);
        min-height: 68px;
        font-size: 1.25rem;
        box-shadow:
            0 18px 36px rgba(7, 18, 38, 0.32),
            inset 0 1px 0 rgba(255, 255, 255, 0.2);
    }

    .secondary-action {
        background:
            linear-gradient(180deg, rgba(22, 43, 74, 0.95), rgba(10, 23, 42, 0.95));
        border-color: rgba(123, 162, 222, 0.22);
        min-height: 60px;
    }

    .compact-action.primary-action,
    .compact-action.secondary-action {
        min-height: 44px;
        font-size: 0.92rem;
    }

    .bag-panel {
        display: grid;
        gap: 6px;
    }

    .green {
        border-color: rgba(115, 235, 124, 0.28);
        background:
            linear-gradient(180deg, rgba(16, 49, 32, 0.92), rgba(9, 28, 19, 0.94));
        box-shadow: inset 4px 0 0 rgba(115, 235, 124, 0.9);
    }

    .green strong {
        color: #73eb7c;
    }

    .yellow {
        background:
            linear-gradient(180deg, rgba(255, 207, 99, 0.95), rgba(208, 149, 48, 0.96));
        color: #36220a;
        border: 1px solid rgba(255, 207, 99, 0.48);
    }

    .red {
        border-color: rgba(255, 118, 111, 0.26);
        background:
            linear-gradient(180deg, rgba(72, 18, 27, 0.92), rgba(46, 9, 18, 0.94));
        box-shadow: inset 4px 0 0 rgba(255, 118, 111, 0.88);
    }

    .red strong {
        color: #ff9b95;
    }

    .neutral {
        border-color: rgba(123, 162, 222, 0.18);
    }

    .pill {
        width: fit-content;
        padding: 0.65rem 0.95rem;
        font-size: 0.82rem;
        font-weight: bold;
        text-align: center;
        border-radius: var(--radius-sm);
        letter-spacing: 0.08em;
    }

    .hidden {
        display: none;
    }

    .notification {
        box-shadow:
            0 0 0 4px rgba(255, 94, 84, 0.12),
            0 16px 44px rgba(0, 0, 0, 0.38);
    }

    #chatbox-container {
        position: fixed;
        right: 28px;
        bottom: calc(var(--tray-reserved-space) + 92px);
        width: min(360px, calc(100vw - 32px));
        z-index: 10;
    }

    #chat-btn {
        position: fixed;
        right: 28px;
        bottom: calc(var(--tray-reserved-space) + 16px);
        min-width: 96px;
        min-height: 54px;
        border-radius: var(--radius-lg);
        z-index: 11;
        background:
            radial-gradient(circle at top left, rgba(255, 207, 99, 0.3), transparent 30%),
            linear-gradient(180deg, rgba(33, 77, 144, 0.98), rgba(15, 41, 84, 0.98));
        border-color: rgba(123, 176, 255, 0.34);
    }

    @media (max-width: 860px) {
        #game {
            --game-edge: 8px;
            --tray-reserved-space: 390px;
            --tray-grid-height: 238px;
            --tile-face-height: 78px;
            --tile-value-size: 1.92rem;
            padding-top: 8px;
        }

        #game.scaleCompact,
        #game.playMode {
            --tray-reserved-space: 368px;
            --tray-grid-height: 222px;
            --tile-face-height: 72px;
            --tile-value-size: 1.72rem;
        }

        #game.playMode {
            --tray-reserved-space: 266px;
        }

        #game.scaleLarge {
            --tray-reserved-space: 420px;
            --tray-grid-height: 260px;
            --tile-face-height: 90px;
            --tile-value-size: 2.18rem;
        }

        .control-dock {
            grid-template-columns: 1fr auto;
            align-items: start;
            gap: 8px;
        }

        .turn-summary {
            grid-column: 1 / -1;
        }

        .quick-actions {
            grid-column: 1 / -1;
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            overflow: visible;
            padding-bottom: 0;
        }

        .mode-button {
            grid-column: 1 / -1;
        }

        .compact-action {
            width: 100%;
            min-width: 0;
            white-space: normal;
        }

        .view-options {
            grid-column: 1;
        }

        #leave-btn {
            grid-column: 2;
        }

        .view-options-panel {
            right: 0;
        }

        #social {
            top: 120px;
            bottom: var(--tray-reserved-space);
        }

        #buttons,
        #sorting,
        #actions {
            grid-template-columns: 1fr;
            flex-direction: column;
            align-items: stretch;
        }

        #buttons {
            align-items: stretch;
        }

        #chatbox-container {
            right: 8px;
            bottom: calc(var(--tray-reserved-space) + 74px);
        }

        #chat-btn {
            right: 8px;
            bottom: calc(var(--tray-reserved-space) + 10px);
            min-width: 86px;
            min-height: 48px;
        }
    }
</style>
